import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from './supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check initial auth state
    const checkUser = async () => {
      try {
        const { user, error } = await auth.getCurrentUser();
        if (user && !error) {
          setUser(user);
        }
      } catch (error) {
        console.error('Error checking user:', error);
      } finally {
        setLoading(false);
      }
    };

    checkUser();

    // Listen for auth state changes
    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    console.log('[signIn] called with:', { email, password });
    const { data, error } = await auth.signIn(email, password);
    console.log('[signIn] response:', { data, error });
    return { data, error };
  };

  const signUp = async (email, password, userData = {}) => {
    console.log('[signUp] called with:', { email, password, userData });
    try {
      const { data, error } = await auth.signUp(email, password, userData);
      console.log('[signUp] response:', { data, error });
      return { data, error };
    } catch (err) {
      console.error('[signUp] exception:', err);
      return { data: null, error: err };
    }
  };

  const signOut = async () => {
    const { error } = await auth.signOut();
    return { error };
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 